import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { store } from './store';
import { wagmiConfig, wagmiAdapter, supportedChains, defaultNetwork, WC_PROJECT_ID } from './config/wagmi';
import { createAppKit } from '@reown/appkit/react';
import { WagmiWeb3Provider } from './contexts/WagmiWeb3Context';
import { TronProvider } from './contexts/TronContext';
import { UnifiedWalletProvider } from './contexts/UnifiedWalletContext';

// Components
import PresaleDashboard from './components/PresaleDashboard';
import NotFound from './components/NotFound';
import ClerkTokenProvider from './components/ClerkTokenProvider';
import Landing from './components/Landing';
import SignInPage from './components/SignInPage';
import SignUpPage from './components/SignUpPage';
import { useUserRole } from './hooks/useUserRole';

// Styles
import './App.css';

// The admin shell (nav, pages, route paths) lives in its own lazy chunk so
// none of it ships in the public bundle. It is only fetched after AdminGate
// has confirmed the signed-in user is an admin.
const AdminApp = React.lazy(() => import('./components/AdminApp'));

// Initialise Reown AppKit once at module load. Drives the unified EVM connect
// modal (MetaMask, Trust extension, Trust mobile via WC, Rainbow, Coinbase, ...).
// The modal is only opened on demand from WagmiWeb3Context.connectWallet().
if (WC_PROJECT_ID) {
  createAppKit({
    adapters: [wagmiAdapter],
    networks: supportedChains,
    // Land WalletConnect wallets (Trust Wallet mobile) on BSC — the delivery
    // chain — instead of the first network in the list (Ethereum). Without this
    // AppKit defaults to supportedChains[0] and every Trust session opens on ETH.
    defaultNetwork,
    // Keep AppKit's session-aware network switcher available so users can move
    // between Ethereum and BSC from the modal in either direction.
    enableNetworkSwitch: true,
    projectId: WC_PROJECT_ID,
    metadata: {
      name: 'VIITUM',
      description: 'VIITUM token platform',
      url: typeof window !== 'undefined' ? window.location.origin : 'https://viitum.io',
      icons: [
        typeof window !== 'undefined'
          ? `${window.location.origin}/logo192.png`
          : 'https://viitum.io/logo192.png',
      ],
    },
    features: {
      analytics: false,
      email: false,
      socials: false,
      onramp: false,
      swaps: false,
    },
  });
} else if (typeof window !== 'undefined') {
  console.warn(
    '[VIITUM] REACT_APP_WALLETCONNECT_PROJECT_ID is not set — AppKit modal disabled. ' +
    'Get a free project ID at https://cloud.reown.com to enable WalletConnect / Trust Wallet.'
  );
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status === 429 || (status >= 400 && status < 500)) return false;
        return failureCount < 2;
      },
    },
  },
});

// Sends signed-out users to /sign-in while preserving the route they tried to reach
// (so Clerk redirects back there after auth). `to` overrides the current location
// when we want a specific destination after sign-in.
function RedirectToSignIn({ to }) {
  const location = useLocation();
  const target = to || `${location.pathname}${location.search}`;
  const url = `/sign-in?redirect_url=${encodeURIComponent(target)}`;
  return <Navigate to={url} replace />;
}

// Defense in depth on top of the backend's requireAdmin gate: non-admin signed-in
// users hitting anything outside the public routes see a plain 404, exactly as if
// the page didn't exist — no redirect that would hint at gated content. Backend
// still 403s the admin APIs regardless.
function AdminGate({ children }) {
  const { isAdmin, isLoading } = useUserRole();
  if (isLoading) return null;
  if (!isAdmin) return <NotFound />;
  return children;
}

function App() {
  return (
    <Provider store={store}>
      <WagmiProvider config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <WagmiWeb3Provider>
            <TronProvider>
              <UnifiedWalletProvider>
                <Router>
                  <Routes>
                    {/* Public landing */}
                    <Route path="/" element={<Landing />} />
                    <Route path="/presale" element={<Navigate to="/presale-v2" replace />} />

                    {/* Clerk auth pages (path-based routing so Clerk owns sub-routes) */}
                    <Route path="/sign-in/*" element={<SignInPage />} />
                    <Route path="/sign-up/*" element={<SignUpPage />} />

                    {/* Presale (Clerk-gated) */}
                    <Route
                      path="/presale-v2"
                      element={
                        <>
                          <SignedOut>
                            <RedirectToSignIn to="/presale-v2" />
                          </SignedOut>
                          <SignedIn>
                            <ClerkTokenProvider>
                              <PresaleDashboard />
                            </ClerkTokenProvider>
                          </SignedIn>
                        </>
                      }
                    />

                    {/* Everything else: signed-out visitors are sent to sign-in
                        with the original path preserved (every unknown path behaves
                        the same, so this confirms nothing about which routes exist).
                        Signed-in non-admins see a plain 404; only admins get the
                        lazily-loaded admin shell. */}
                    <Route
                      path="*"
                      element={
                        <>
                          <SignedOut>
                            <RedirectToSignIn />
                          </SignedOut>

                          <SignedIn>
                            <ClerkTokenProvider>
                              <AdminGate>
                                <React.Suspense fallback={null}>
                                  <AdminApp />
                                </React.Suspense>
                              </AdminGate>
                            </ClerkTokenProvider>
                          </SignedIn>
                        </>
                      }
                    />
                  </Routes>
                </Router>
              </UnifiedWalletProvider>
            </TronProvider>
          </WagmiWeb3Provider>
        </QueryClientProvider>
      </WagmiProvider>
    </Provider>
  );
}

export default App;
